import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { BehaviorSubject , Observable } from 'rxjs';
import { ContractOverage, ContractOverageResults, GlobalTenantContractsApi  } from 'src/app/usage-api';
import { BaseDataSource, TableColumn } from './datasource';

export type FindContractOveragesRequest = {
  tenantId?: string;
  contractId?: string;
  searchAllTenants?: boolean;
  onlyActiveTenants?: boolean;
};
export class ContractsOverageDataSource extends BaseDataSource<ContractOverage, GlobalTenantContractsApi, FindContractOveragesRequest> {
  columns: TableColumn<ContractOverage>[] = [
    {
      field: 'tenantName',
      localizationKey: 'BackOffice.ManageContracts.Contract.TenantName',
      link: (val: ContractOverage) => {
        return {
          route: ['/back-office/contracts', val.tenantId, val.contractId],
          queryParams:  {tab: 'usage-overages'} ,
          queryParamsHandling: 'merge'
        }
      },
      style: { 'max-width': '12rem', 'min-width': '12rem', width: '12rem' },
      sticky: true,
      noSort: true,
    },
    {
      field: 'tenantId',
      localizationKey: 'BackOffice.ManageContracts.Contract.TenantId',
      style: { 'max-width': '8rem', 'min-width': '8rem', width: '8rem' },
      noSort: true,
    },

    {
      field: 'startDate',
      localizationKey: 'BackOffice.ManageContracts.Contract.ContractStartDate',
      render: (val) => {
        if (val.startDate) {
          const date = new Date(val.startDate);
          return date.toLocaleDateString();
        }
        return '';
      },
      style: { 'max-width': '10rem', 'min-width': '10rem', width: '10rem' },
      noSort: true,
    },
    {
      field: 'endDate',
      localizationKey: 'BackOffice.ManageContracts.Contract.ServiceEndDate',
      render: (val) => {
        if (val.endDate) {
          const date = new Date(val.endDate);
          return date.toLocaleDateString();
        }
        return '';
      },
      style: { 'max-width': '10rem', 'min-width': '10rem', width: '10rem' },
      noSort: true,
    }
  ];

  get availableColumns(): TableColumn<ContractOverage>[] {
    return this.columns;
  }

  get queryArguments(): object {
    const request = this.currentRequest.value;
    return {
      tenantId: request.tenantId,
      contractId: request.contractId,
      searchAllTenants: request.searchAllTenants,
      onlyActiveTenants: request.onlyActiveTenants
    };
  }

  brandSearchTerm$ = new BehaviorSubject<string>(null);
  isActiveSearchTerm$ = new BehaviorSubject<boolean>(null);

  previousRequest: FindContractOveragesRequest;

  constructor(service: GlobalTenantContractsApi, paginator: MatPaginator, sort: MatSort, request: FindContractOveragesRequest, loadOnInit: boolean = true) {
    super(service, paginator, sort, request, loadOnInit);
  }

  override init(setInit: boolean = false) {
    super.init(setInit);

    this.subscriptions.add(this.createSubscription(this.brandSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.isActiveSearchTerm$));

    this.hasInit = true;
  }

  public override loadData(forceRefresh: boolean = false) {
    if (!this.hasInit) {
      return;
    }

    const currentRequest = this.currentRequest.value ?? {};
    currentRequest.onlyActiveTenants = true;
    currentRequest.searchAllTenants = true;
    currentRequest.tenantId = null;


    if (this.previousRequest && !forceRefresh && this.isEqual(this.previousRequest, currentRequest)) {
      return;
    }

    this.currentRequest.next(currentRequest);
    this.loadingSubject.next(true);
    this.hasError.next(false);
    this.errorMessage.next(null);

    this.previousRequest = { ...currentRequest };

    const request$: Observable<ContractOverageResults> = this.service.globalTenantContractsFindTenantContractOverages(
      currentRequest.tenantId,
      currentRequest.contractId,
      currentRequest.onlyActiveTenants,
      currentRequest.searchAllTenants
    );

    this.handleResponse(request$, 'BackOffice.ManageContracts.Table.ErrorMessage').subscribe();
 }

  isEqual(a: FindContractOveragesRequest, b: FindContractOveragesRequest): boolean {
    return (
      a.onlyActiveTenants === b.onlyActiveTenants &&
      a.searchAllTenants === b.searchAllTenants &&
      a.tenantId === b.tenantId &&
      a.contractId === b.contractId
    );
  }
}
